import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NAV } from "@/constants/testIds";

const LINKS = [
  { id: "funzionalita", label: "Funzionalità" },
  { id: "come-funziona", label: "Come funziona" },
  { to: "/shop", label: "Shop" },
  { id: "prezzi", label: "Prezzi" },
  { id: "faq", label: "FAQ" },
  { id: "contatti", label: "Contatti" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const logoClicksRef = useRef(0);
  const logoTimerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Accesso nascosto all'area riservata: 5 clic rapidi sul logo -> /admin.
  // Un clic singolo mantiene il comportamento normale (torna alla home).
  const handleLogoClick = () => {
    logoClicksRef.current += 1;
    if (logoTimerRef.current) clearTimeout(logoTimerRef.current);

    if (logoClicksRef.current >= 5) {
      logoClicksRef.current = 0;
      navigate("/admin");
      return;
    }
    logoTimerRef.current = setTimeout(() => { logoClicksRef.current = 0; }, 1200);

    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const go = (l) => {
    setOpen(false);
    if (typeof l === "object" && l.to) {
      navigate(l.to);
      return;
    }
    const id = typeof l === "object" ? l.id : l;
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid={NAV.navbar}
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <nav className="container-x flex h-16 items-center justify-between gap-4">
        <button
          data-testid={NAV.logo}
          onClick={handleLogoClick}
          className="group flex items-center"
        >
          <span className="brand-animated font-display font-bold text-xl sm:text-2xl tracking-tight">
            Controlla Telefoni
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l)}
              className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button
            onClick={() => go("prezzi")}
            data-testid={NAV.buyNow}
            className="hidden sm:inline-flex rounded-xl glow-hover"
          >
            Acquista ora
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden rounded-xl" data-testid={NAV.mobileMenu} aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[78%] sm:w-80">
              <div className="mt-8 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <button
                    key={l.label}
                    onClick={() => go(l)}
                    className="text-left px-3 py-3 text-base font-medium rounded-lg hover:bg-secondary transition-colors"
                  >
                    {l.label}
                  </button>
                ))}
                <Button onClick={() => go("prezzi")} className="mt-4 rounded-xl">
                  Acquista ora
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
